import {
  useState,
  useContext,
  useEffect,
  type ChangeEvent,
} from 'react';
import { Switch, Route, matchPath } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';

import type { UserCredential } from 'firebase/auth';
import type { FirebaseError } from 'firebase/app';

import registerNamespace from './locales';
import availableLanguages from '../../juristec-ui/utils/language/availableLanguages';
import firebase from '../../utils/firebase';
import Multifactor from '../../utils/multifactor';
import {
  getRefUser, getBatch, getRefSession, getRefUserHistory,
} from '../../utils/firestore';
import uuidv4 from '../../juristec-ui/utils/functions/randomUUID';
import firebaseErrorCodesMap from './firebaseErrorCodes';
import type {
  LoginPageProps,
  EmailState,
  PasswordState,
  ErrorAlertModal,
  UserData,
  UpdateUserData,
} from './types';

import { AuthContext } from '../../context/AuthProvider';
import { UsersContext } from '../../context/UsersProvider';
import { AlertContext } from '../../context/AlertProvider';
import { ModalContext } from '../../context/ModalProvider';
import useRecaptchaV3 from '../../hooks/useRecaptchaV3';
import useRecaptchaV2 from '../../hooks/useRecaptchaV2';
import useSessionStorage from '../../hooks/useSessionStorage';

import FSPolicyContainer from '../../components/ProgressiveModals/Policy/Steps/FirstStep';
import SSTermsContainer from '../../components/ProgressiveModals/Policy/Steps/SecondStep';
import TSTermsContainer from '../../components/ProgressiveModals/Policy/Steps/ThirdStep';
import MfaLogin from '../../components/ProgressiveModals/MfaLogin';
import CookiesMessage from '../../components/CookiesMessage';
import Loader from '../../juristec-ui/core/Loader';
import IconButton from '../../juristec-ui/core/IconButton';
import Tooltip from '../../juristec-ui/core/Tooltip';

import { ArrowLeft } from '../../juristec-ui/icons';

import {
  CustomProvider,
  EmailDefault,
  FirebaseLogin,
  ResetPassword,
} from './layers';

import {
  MainContainer,
  FormContainer,
  Nav,
  Logo,
  TextOptions,
  TextOptionsLink,
  LanguageButton,
  MultiButton,
} from './styled';

const namespace = registerNamespace('src/views/LoginPage2/index.tsx');

const InfoMap = {
  privacyPolicyModalTitle: FSPolicyContainer,
  consentPolicyModalTitle: SSTermsContainer,
  termsOfUseModalTitle: TSTermsContainer,
} as const;

const stopAutoSubmit = (e: ChangeEvent<HTMLFormElement>) => {
  e.preventDefault();
  e.stopPropagation();
};

const LoginPage = ({
  history,
}: LoginPageProps) => {
  const { t, i18n } = useTranslation(namespace);
  const theme = useTheme() as Record<string, string>;
  const recaptcha = useRecaptchaV3();
  const v2Verifier = useRecaptchaV2();
  const [, setSession] = useSessionStorage('session', null);

  const {
    currentUser,
    checkRecaptchaToken,
    getLoginProviderByEmail,
    setupUserSession,
  } = useContext(AuthContext);
  // @ts-expect-error not typed yet
  const { usersAPI } = useContext(UsersContext);
  // @ts-expect-error not typed yet
  const { setModalConfig, closeModal } = useContext(ModalContext);
  // @ts-expect-error not typed yet
  const { setAlertConfig } = useContext(AlertContext);

  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<EmailState>({ value: '', error: true, errorMsg: '' });
  const [password, setPassword] = useState<PasswordState>({ value: '', error: true, errorMsg: '' });
  const [providerId, setProviderId] = useState<string>('');

  useEffect(() => {
    if (currentUser) {
      const goto = new URLSearchParams(window.location.search);
      history.replace(goto.get('to') ?? '/home');
    }
  }, [currentUser, history]);

  const handleLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const errorAlertModal = ({
    childKey = '', textKey = 'errorAccessNotAllowed-text', errorObj,
  }: ErrorAlertModal) => {
    const alertConfig = {
      type: 'error', text: t(textKey), child: t(childKey),
    };
    if (errorObj) {
      if (errorObj.code === 'auth/internal-error' && errorObj.message?.includes('IP address not allowed.')) {
        alertConfig.child = t('errorDomainNotAllowed-child');
      } else {
        alertConfig.child = t(`${firebaseErrorCodesMap(errorObj)}-child`, { errorMsg: errorObj.message });
      }
    }
    setLoading(false);
    setAlertConfig(alertConfig);
  };

  const infoModal = (key: keyof typeof InfoMap) => {
    const InfoContainer = InfoMap[key];
    setModalConfig({
      title: t(key),
      children: (
        <div style={{ marginBottom: '10px' }}>
          <InfoContainer readOnly />
        </div>
      ),
    });
  };
  const accessPrivacyPolicy = () => infoModal('privacyPolicyModalTitle');
  const consentTerms = () => infoModal('consentPolicyModalTitle');
  const useTerms = () => infoModal('termsOfUseModalTitle');

  // TODO TRANSLATION
  const CreateInstanceFirstLoginMessages = [
    `${t('loadMsg1')}.`,
    `${t('loadMsg1')}..`,
    `${t('loadMsg1')}...`,
    t('loadMsg2'),
    t('loadMsg3'),
    t('loadMsg4'),
    t('loadMsg5'),
    `${t('loadMsg1')}.`,
    `${t('loadMsg1')}..`,
    `${t('loadMsg1')}...`,
  ];

  const handlePathChange = (endpoint: string) => {
    const urlParams = new URLSearchParams(window.location.search);
    history.push({
      pathname: endpoint,
      search: urlParams.toString(),
    });
  };

  const goBack = () => handlePathChange('/login');

  const afterEmailPassword = () => {
    handlePathChange('/login/password');
  };

  const afterEmailProvider = async () => {
    setLoading(true);
    const gToken = await recaptcha.getToken('login');
    const res = await getLoginProviderByEmail(email.value, gToken);
    if (res.error) {
      errorAlertModal({});
      return;
    }
    if (!res.providerId) {
      setEmail((e) => ({
        ...e,
        errorMsg: t('noLoginProviderFoundedErrorMsg'),
      }));
      setLoading(false);
      return;
    }
    setLoading(false);
    handlePathChange(`/login/${res.providerId}`);
  };

  const successLogin = async (loginStatus: UserCredential) => {
    if (!loginStatus.user.emailVerified) {
      errorAlertModal({
        textKey: 'errorEmailConfirmation-text',
        childKey: 'errorEmailEmailConfirmation-child',
      });
    }
    const refUser = getRefUser(loginStatus.user.uid);
    refUser.get().then(async (doc) => {
      const batch = getBatch();
      const sessionId = uuidv4();
      const updateData: UpdateUserData = {
        lastLogin: loginStatus.user.metadata.lastSignInTime ? new Date(
          loginStatus.user.metadata.lastSignInTime,
        ) : new Date(),
        session: sessionId,
      };
      let userData: UserData = {};
      if (doc.exists()) {
        userData = doc.data();
        if (!loginStatus.user.displayName) {
          firebase.updateProfile({
            displayName: userData?.name || 'Waiting...',
          });
        }
      }
      if (!userData.configs?.language) {
        updateData.configs = {
          ...(userData.configs ?? {}),
          language: i18n.language?.split('-')[0] || 'pt',
        };
      }
      const mail = loginStatus.user.email ?? (email.error ? null : email.value);
      if (mail && userData.email !== mail) updateData.email = mail;
      batch.set(refUser, updateData, { merge: true });
      batch.set(getRefSession(loginStatus.user.uid), { session: sessionId });
      // @ts-expect-error not typed yet
      setSession(sessionId);
      const refUserHistory = getRefUserHistory(loginStatus.user.uid);
      const refUserHistoryDoc = refUserHistory.doc(sessionId);
      batch.set(refUserHistoryDoc, {
        date: new Date(),
        session: sessionId,
      });
      await batch.commit();
      await setupUserSession(loginStatus.user);
    });
  };

  const checkCaptcha = async () => {
    const gToken = await recaptcha.getToken('login');
    const v3Validator = await checkRecaptchaToken(gToken);
    if (v3Validator.error) {
      if (v3Validator.raw === 'forbidden') errorAlertModal({ childKey: 'errorBadRep-child' });
      else errorAlertModal({ childKey: 'errorBackend-child', textKey: 'errorBackend-text' });
      return false;
    }
    return true;
  };

  const handleMultifactor = (error: FirebaseError) => {
    const mfa = new Multifactor(
      v2Verifier.getVerifier(), error,
    ).success((loginMStatus: UserCredential) => {
      successLogin(loginMStatus);
    }).error((er: FirebaseError) => {
      errorAlertModal({ errorObj: er });
    });
    /* Modal sendCode function */
    setModalConfig({
      title: t('multifactorModalTitle'),
      children: (
        <MfaLogin
          hide={closeModal}
          mfa={mfa}
          setLoading={setLoading as () => void}
        />
      ),
    });
  };

  const firebaseLogin = async (loginFunc: () => Promise<UserCredential>) => {
    try {
      setLoading(true);
      // REcaptcha
      if (!(await checkCaptcha())) return;
      loginFunc().then((loginStatus) => {
        successLogin(loginStatus);
      }).catch((error: FirebaseError) => {
        if (error.code === 'auth/multi-factor-auth-required') {
          setLoading(false);
          handleMultifactor(error);
        } else {
          errorAlertModal({ errorObj: error as FirebaseError });
        }
      });
    } catch (error) {
      errorAlertModal({ errorObj: error as FirebaseError });
    }
  };

  const passwordLogin = async () => {
    if (email.error || password.error) {
      errorAlertModal({ childKey: 'errorWrongCreds-child' });
      return;
    }
    // Firebase email + password login
    firebaseLogin(() => firebase.login(email.value, password.value));
  };

  const providerLogin = async () => {
    if (!providerId) {
      errorAlertModal({ childKey: 'errorWrongProvider-child' });
      return;
    }
    // Firebase custom provider login
    firebaseLogin(() => firebase.providerLogin(providerId));
  };

  const handleResetPasswordLink = async () => {
    if (email.error) {
      errorAlertModal({ textKey: 'errorInvalidEmail-text' });
      return;
    }
    try {
      setLoading(true);
      const gToken = await recaptcha.getToken('reset_password');
      const res = await usersAPI.resetPassword(email.value.trim(), gToken);
      if (res.error && res.status === 500) {
        errorAlertModal({ textKey: 'errorBackend-text', childKey: res.raw });
        return;
      }
      setLoading(false);
      setAlertConfig({
        type: 'success',
        text: t('successResetPasswordLink-text'),
        child: t('successResetPasswordLink-child'),
        okFunction: () => {
          setEmail({ value: '', error: true, errorMsg: '' });
          history.push('/login');
        },
      });
    } catch (error) {
      console.log(error);
      errorAlertModal({
        textKey: 'errorBackend-text', childKey: 'errorInternalError-child',
      });
    }
  };

  return (
    <>
      <MainContainer>
        {(loading || !recaptcha.ready) && (
          // @ts-expect-error not typed yet
          <Loader underText={CreateInstanceFirstLoginMessages} />
        )}
        <FormContainer onSubmit={stopAutoSubmit}>
          <Nav>
            {!matchPath(history.location.pathname, {
              path: ['/', '/login'], exact: true,
            }) && (
              <Tooltip text={t('returnTip')} direction="right" className="go-back-tooltip">
                <IconButton
                  type="button"
                  variant="outlined"
                  color="grey"
                  size="medium"
                  shape="rounded"
                  onClick={goBack}
                >
                  <ArrowLeft />
                </IconButton>
              </Tooltip>
            )}
            <MultiButton>
              {/* Apenas o end do DEV possuí esse token */}
              {(
                !!process.env.REACT_APP_APPCHECK_DEBUG_TOKEN
              ) && availableLanguages.map((language) => (
                <Tooltip
                  key={language.value}
                  text={language.label}
                  direction="top"
                  atModal
                >
                  <LanguageButton
                    type="button"
                    selected={i18n.language?.includes(language.value)}
                    onClick={() => handleLanguage(language.value)}
                  >
                    <img
                      src={`https://flagcdn.com/24x18/${language.flag}.webp`}
                      width="20px"
                      height="15px"
                      alt={language.label}
                    />
                  </LanguageButton>
                </Tooltip>
              ))}
            </MultiButton>
          </Nav>
          <Logo alt="logo" src={theme.loginLogo || undefined} />
          <Switch>
            <Route
              exact
              path={['/', '/login']}
              render={() => (
                <EmailDefault
                  emailState={[email, setEmail]}
                  passwordState={[password, setPassword]}
                  afterEmailPassword={afterEmailPassword}
                  afterEmailProvider={afterEmailProvider}
                  t={t}
                />
              )}
            />
            <Route
              exact
              path="/login/password"
              render={() => (
                <FirebaseLogin
                  emailState={[email, setEmail]}
                  passwordState={[password, setPassword]}
                  passwordLogin={passwordLogin}
                  goBack={goBack}
                  t={t}
                />
              )}
            />
            <Route
              exact
              path="/login/reset_password"
              render={() => (
                <ResetPassword
                  emailState={[email, setEmail]}
                  handleResetPasswordLink={handleResetPasswordLink}
                  t={t}
                />
              )}
            />
            <Route
              exact
              path="/login/:provider"
              render={() => (
                <CustomProvider
                  setProviderId={setProviderId}
                  providerLogin={providerLogin}
                  goBack={goBack}
                  t={t}
                />
              )}
            />
          </Switch>
          <div style={{ marginTop: 'auto' }}>
            <TextOptions>
              <Trans
                t={t}
                i18nKey="juristecPolicy"
                components={{
                  consent: <TextOptionsLink onClick={consentTerms} />,
                  use: <TextOptionsLink onClick={useTerms} />,
                  privacy: <TextOptionsLink onClick={accessPrivacyPolicy} />,
                }}
              />
            </TextOptions>
          </div>
        </FormContainer>
      </MainContainer>
      <CookiesMessage />
    </>
  );
};

export default LoginPage;
